import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput.jsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import { readPage } from '../../../utils/apiResult';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 440px;
  width: 100%;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const CardTitle = styled.h3`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// Task 35 — havuz sayfasında sekme olarak sunulan tenant etiket sözlüğü (TalentTag).
// Etiketler havuzdan bağımsızdır (bir çalışan havuz üyeliğinden bağımsız etiketlenebilir);
// yönetimi burada, üye satırındaki "Etiket Yönet" ataması ise PoolMembersTab'de.
export default function PoolTagsTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('TD');

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const [removeConfirm, setRemoveConfirm] = useState({ open: false, id: null, name: '' });
  const [removing, setRemoving] = useState(false);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // TD-23 (kalan yüzeyler, Faz 2) — bkz. utils/apiResult.js. Etiket listesi
  // boş görünmek ile YÜKLENEMEMEK arasındaki fark burada özellikle önemli:
  // etiketler üye filtrelemesini besler, eksik liste yanlış filtreleme demektir.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/talent-pool/tags')
      .then((res) => {
        const { ok, items } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const openAdd = useCallback(() => {
    setEditingTag(null);
    setName('');
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setEditingTag(row);
    setName(row.name);
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editingTag) {
        await api.put(`/v1/talent-pool/tags/${editingTag.id}`, { name });
      } else {
        await api.post('/v1/talent-pool/tags', { name });
      }
      setModalOpen(false);
      refresh();
    } catch {
      // handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [editingTag, name, refresh]);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoving(true);
    try {
      await api.post(`/v1/talent-pool/tags/${removeConfirm.id}/remove`);
      setRemoveConfirm({ open: false, id: null, name: '' });
      refresh();
    } catch {
      // handled by interceptor
    } finally {
      setRemoving(false);
    }
  }, [removeConfirm.id, refresh]);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('performance.common.edit'), onClick: openEdit },
      { type: 'divider' },
      { type: 'remove', label: t('common.delete'), onClick: (row) => setRemoveConfirm({ open: true, id: row.id, name: row.name }) },
    ];
  }, [t, canManage, openEdit]);

  const columns = useMemo(() => {
    const cols = [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'name', label: t('performance.talentpool.tagName'), options: { filter: false, sort: false } },
    ];
    if (actionOptions.length > 0) {
      cols.push({
        name: 'rowActions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      });
    }
    return cols;
  }, [t, actionOptions, data]);

  return (
    <div>
      {loadFailed && <LoadErrorBanner />}
      <Overlay $open={modalOpen}>
        {modalOpen && (
          <Card>
            <CardTitle>{editingTag ? t('performance.common.edit') : t('performance.talentpool.addTag')}</CardTitle>
            <TextInput
              label={t('performance.talentpool.tagName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Actions>
              <Button variant="outline" onClick={() => setModalOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={!name.trim() || saving} loading={saving}>{t('common.save')}</Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      <ConfirmDialog
        isOpen={removeConfirm.open}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: removeConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveConfirm({ open: false, id: null, name: '' })}
        loading={removing}
      />

      <DataTable
        title={t('performance.talentpool.tabTags')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        pageSize={20}
        canCreateNewRecord={canManage}
        newButtonLabel={t('performance.talentpool.addTag')}
        onNewButtonClick={openAdd}
      />
    </div>
  );
}
